// =============================================================================
// Origin UI — src/components/compliance/SubmissionViewer.tsx
// CM-11 — read-only viewer for a single submitted form. Fetches the submission
// detail (answers + the schema snapshot it was filled against) and renders
// label → value pairs. Used by the Compliance dashboard drill-in and the
// employee Profile → Compliance tab. Access is enforced server-side
// (self, or any submission for Compliance).
// Print button opens a clean popup for printing individual submissions.
// =============================================================================

import { useEffect, useState } from 'react';
import { complianceApi } from '@/services/complianceApi';
import type {
  ComplianceSubmissionDetail,
  ComplianceFormField,
} from '@/services/complianceApi';
import styles from './SubmissionViewer.module.css';

interface SubmissionViewerProps {
  submissionId: string;
  onClose: () => void;
}

function formatValue(field: ComplianceFormField, raw: unknown): string {
  if (raw === null || raw === undefined || raw === '') return '—';

  switch (field.type) {
    case 'Confirmation':
      return raw === true ? 'Confirmed' : 'Not confirmed';
    case 'Currency':
      return `USD ${raw}`;
    case 'SingleSelect': {
      const opt = (field.options ?? []).find((o) => o.value === raw);
      return opt ? opt.label : String(raw);
    }
    case 'MultiSelect': {
      const arr = Array.isArray(raw) ? raw : [];
      const labels = arr.map((v) => {
        const opt = (field.options ?? []).find((o) => o.value === v);
        return opt ? opt.label : String(v);
      });
      return labels.length > 0 ? labels.join(', ') : '—';
    }
    default:
      return String(raw);
  }
}

function handlePrint(
  detail: ComplianceSubmissionDetail,
  fields: ComplianceFormField[],
  answers: Record<string, unknown>
) {
  const inputFields = fields.filter((f) => f.type !== 'StaticText');
  const submittedDate = new Date(detail.submittedDate).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const rows = inputFields
    .map((field) => {
      const val = formatValue(field, answers[field.key]);
      const escapedLabel = field.label.replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const escapedVal = val.replace(/</g, '&lt;').replace(/>/g, '&gt;');
      return `<tr><td style="padding:8px 12px;font-weight:600;color:#0A2240;vertical-align:top;white-space:nowrap;border-bottom:1px solid #e5e7eb;">${escapedLabel}</td><td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;">${escapedVal}</td></tr>`;
    })
    .join('');

  const html = `<!DOCTYPE html>
<html><head><title>${detail.title} — ${detail.employeeName}</title>
<style>
  body { font-family: Arial, sans-serif; margin: 40px; color: #333; }
  h1 { color: #0A2240; font-size: 20px; margin: 0 0 4px; }
  .meta { color: #666; font-size: 13px; margin-bottom: 24px; }
  table { border-collapse: collapse; width: 100%; }
  .footer { margin-top: 32px; font-size: 11px; color: #999; border-top: 1px solid #ccc; padding-top: 8px; }
  @media print { body { margin: 20px; } }
</style></head><body>
<h1>${detail.title}</h1>
<div class="meta">${detail.employeeName} · ${detail.submissionType} · Version ${detail.versionNumber} · ${submittedDate}</div>
<table>${rows}</table>
<div class="footer">Printed from Origin — OQ Trading Compliance · ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
<script>window.onload = function() { window.print(); }</script>
</body></html>`;

  const win = window.open('', '_blank', 'width=800,height=600');
  if (win) {
    win.document.write(html);
    win.document.close();
  }
}

export function SubmissionViewer({ submissionId, onClose }: SubmissionViewerProps) {
  const [detail, setDetail] = useState<ComplianceSubmissionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = await complianceApi.getSubmissionDetail(submissionId);
        if (!cancelled) setDetail(data);
      } catch (err) {
        console.warn('Failed to load submission', err);
        if (!cancelled) setError('Could not load this submission.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [submissionId]);

  let fields: ComplianceFormField[] = [];
  let answers: Record<string, unknown> = {};
  if (detail) {
    if (detail.formSchema) {
      try {
        const parsed = JSON.parse(detail.formSchema) as ComplianceFormField[];
        if (Array.isArray(parsed)) fields = parsed;
      } catch {
        /* ignore — fall back to empty */
      }
    }
    if (detail.answers) {
      try {
        answers = JSON.parse(detail.answers) as Record<string, unknown>;
      } catch {
        /* ignore */
      }
    }
  }

  const inputFields = fields.filter((f) => f.type !== 'StaticText');

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
        <div className={styles.dialogHeader}>
          <h2 className={styles.dialogTitle}>{detail ? detail.title : 'Submission'}</h2>
          <button className={styles.closeBtn} onClick={onClose} title="Close">
            ✕
          </button>
        </div>

        {loading && <div className={styles.loading}>Loading&hellip;</div>}
        {!loading && error && <div className={styles.error}>{error}</div>}

        {!loading && !error && detail && (
          <div className={styles.body}>
            <div className={styles.meta}>
              <span>{detail.employeeName}</span>
              <span>
                {detail.submissionType} · v{detail.versionNumber} ·{' '}
                {new Date(detail.submittedDate).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })}
              </span>
            </div>

            {inputFields.length === 0 ? (
              <div className={styles.error}>The form definition is unavailable.</div>
            ) : (
              <dl className={styles.answers}>
                {inputFields.map((field) => (
                  <div key={field.key} className={styles.answerRow}>
                    <dt className={styles.answerLabel}>{field.label}</dt>
                    <dd className={styles.answerValue}>
                      {formatValue(field, answers[field.key])}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        )}

        <div className={styles.dialogFooter}>
          {!loading && !error && detail && (
            <button
              className={styles.footerBtn}
              onClick={() => handlePrint(detail, fields, answers)}
              style={{ marginRight: 'auto' }}
            >
              🖨️ Print
            </button>
          )}
          <button className={styles.footerBtn} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
